import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Appointments from "./pages/Appointments";
import Patients from "./pages/Patients";
import PatientDetail from "./pages/PatientDetail";
import Billing from "./pages/Billing";
import BillingUpgrade from "./pages/BillingUpgrade";
import AdminDashboard from "./pages/AdminDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Settings from "./pages/Settings";
import DocumentTemplates from "./pages/DocumentTemplates";
import BillingNew from "./pages/BillingNew";
import Team from "./pages/Team";
import Schedules from "./pages/Schedules";
import Reports from "./pages/Reports";
import Queue from "./pages/Queue";
import PublicInvoice from "./pages/PublicInvoice";
import MicrositeLayout from "./pages/microsite/MicrositeLayout";
import MicrositeHome from "./pages/microsite/MicrositeHome";
import MicrositeAbout from "./pages/microsite/MicrositeAbout";
import MicrositeDoctors from "./pages/microsite/MicrositeDoctors";
import MicrositeServices from "./pages/microsite/MicrositeServices";
import MicrositeContact from "./pages/microsite/MicrositeContact";
import MicrositeBook from "./pages/microsite/MicrositeBook";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import BlogIndex from "./pages/blog/BlogIndex";
import BlogPost from "./pages/blog/BlogPost";

// Block receptionist access to billing
function RoleGuard({ children, allow }) {
  const { user } = useAuth();
  if (user && allow && !allow.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Super admin landing redirect
function PostLoginRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === "super_admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

// Subdomain microsite detection — if host is <slug>.hedesk.in render microsite at root
function detectMicrositeSlug() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  // Match {slug}.hedesk.in or {slug}.hedesk.help (any future apex), but NOT www/app/admin
  const m = host.match(/^([a-z0-9-]+)\.(hedesk\.(in|help))$/i);
  if (!m) return null;
  const candidate = m[1].toLowerCase();
  if (["www", "app", "admin", "api", "dashboard"].includes(candidate)) return null;
  return candidate;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const micrositeSlug = detectMicrositeSlug();
  if (micrositeSlug) {
    // Strip leading slash from path so microsite sub-routes work via base path = /
    const sub = location.pathname.replace(/^\/+/, "");
    return (
      <Routes>
        <Route path="/" element={<MicrositeLayout subdomainSlug={micrositeSlug} />}>
          <Route index element={<MicrositeHome />} />
          <Route path="about" element={<MicrositeAbout />} />
          <Route path="doctors" element={<MicrositeDoctors />} />
          <Route path="services" element={<MicrositeServices />} />
          <Route path="contact" element={<MicrositeContact />} />
          <Route path="book" element={<MicrositeBook />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/invoice/:token" element={<PublicInvoice />} />
      {/* Public clinic microsite */}
      <Route path="/c/:slug" element={<MicrositeLayout />}>
        <Route index element={<MicrositeHome />} />
        <Route path="about" element={<MicrositeAbout />} />
        <Route path="doctors" element={<MicrositeDoctors />} />
        <Route path="services" element={<MicrositeServices />} />
        <Route path="contact" element={<MicrositeContact />} />
        <Route path="book" element={<MicrositeBook />} />
      </Route>

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["super_admin"]}>
              <AdminDashboard />
            </RoleGuard>
          </ProtectedRoute>
        }
      />

      <Route
        path="/post-login"
        element={
          <ProtectedRoute>
            <PostLoginRedirect />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor", "receptionist"]}>
              <AppLayout><Dashboard /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor", "receptionist"]}>
              <AppLayout><Appointments /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor", "receptionist"]}>
              <AppLayout><Patients /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/:id"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor", "receptionist"]}>
              <AppLayout><PatientDetail /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor", "receptionist"]}>
              <AppLayout><Billing /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/new"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor", "receptionist"]}>
              <AppLayout><BillingNew /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/upgrade"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor"]}>
              <AppLayout><BillingUpgrade /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor"]}>
              <AppLayout><Team /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedules"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor"]}>
              <AppLayout><Schedules /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor"]}>
              <AppLayout><Reports /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/queue"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor", "receptionist"]}>
              <AppLayout><Queue /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor"]}>
              <AppLayout><Settings /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/templates"
        element={
          <ProtectedRoute>
            <RoleGuard allow={["admin", "doctor"]}>
              <AppLayout><DocumentTemplates /></AppLayout>
            </RoleGuard>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
